import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Avatar,
    Box,
    Chip,
    Paper,
    Slide,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import ApartmentIcon from '@mui/icons-material/ApartmentOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ViewTenantDetailsModal = ({ open, handleClose, tenant }) => {
    if (!tenant) return null;

    const getValue = (value) => value || 'N/A';

    const InfoItem = ({ icon, label, value }) => (
        <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1, alignItems: 'flex-start', py: 1 }}>
            <Box sx={{ color: 'primary.main', mt: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.3, textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', wordBreak: 'break-word' }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header with Gradient Background */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #487fff 0%, #6366f1 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                {/* Background Pattern */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <DialogTitle sx={{ p: 2.5, pb: 0, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                                Tenant Details
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                {/* Profile Section in Header */}
                <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Avatar
                            sx={{
                                width: 72,
                                height: 72,
                                bgcolor: 'white',
                                color: 'primary.main',
                                fontSize: '1.75rem',
                                fontWeight: 700,
                                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                            }}
                        >
                            {tenant?.name?.charAt(0)?.toUpperCase() || 'T'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                                {getValue(tenant?.name)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label={`ID: ${tenant?.id || 'N/A'}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                    }}
                                />
                                <Chip
                                    label={tenant?.status ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                        bgcolor: tenant?.status ? '#22c55e' : '#ef4444',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                    }}
                                />
                                <Chip
                                    label={`Flat: ${tenant?.flatNumber}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                {/* Main Content Grid */}
                <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                        {/* Contact Information */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Contact Information
                                </Typography>
                            </Box>
                            <InfoItem icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Name" value={getValue(tenant?.name)} />
                            <InfoItem icon={<PhoneIcon sx={{ fontSize: 18 }} />} label="Phone" value={getValue(tenant?.phone)} />
                            <InfoItem icon={<EmailIcon sx={{ fontSize: 18 }} />} label="Email" value={getValue(tenant?.email)} />
                        </Paper>

                        {/* Apartment Information */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <ApartmentIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Apartment Information
                                </Typography>
                            </Box>
                            <InfoItem icon={<ApartmentIcon sx={{ fontSize: 18 }} />} label="Flat Number" value={getValue(tenant?.flatNumber)} />
                            <InfoItem icon={<ApartmentIcon sx={{ fontSize: 18 }} />} label="Building" value={getValue(tenant?.building)} />
                            <InfoItem icon={<ApartmentIcon sx={{ fontSize: 18 }} />} label="Community" value={getValue(tenant?.society)} />
                        </Paper>

                        {/* Status Information */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Status
                                </Typography>
                            </Box>
                            <InfoItem icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Current Status" value={tenant?.status ? 'Active' : 'Inactive'} />
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Chip label={tenant?.status ? 'Active' : 'Inactive'} size="small" sx={{
                                    bgcolor: tenant?.status ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    color: tenant?.status ? '#22c55e' : '#ef4444'
                                }} />
                            </Box>
                        </Paper>

                        {/* Additional Information */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <CalendarTodayIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Additional Information
                                </Typography>
                            </Box>
                            <InfoItem icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />} label="Created On" value={new Date(tenant?.createdOn).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} />
                            <InfoItem icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />} label="Joined Date" value={new Date(tenant?.createdOn).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} />
                        </Paper>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ViewTenantDetailsModal;
